import { SchemaService, ServerCommunicationService } from '@amzn/awscat-portfolio-assessment-service-client';
import { SchemaProperty, ServerCommBulkDeleteParams, ServerCommBulkUpdateParams, ServerCommunication } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { SortingState } from '@amzn/awsui-collection-hooks/dist/cjs/interfaces';
import { SpaceBetween } from '@amzn/awsui-components-react';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../common/Localization';
import AWSCATTable from '../../common/table/Table-index';
import { ColumnDefinition } from '../../common/table/types';
import { MatchParam } from '../../common/types';
import { deepCopy, generatePluralStr } from '../../common/util';
import PortfolioTableActionGroup from './common/PortfolioTableActionGroup';
import { ColumnOrderType, EXCLUDED_SERVER_COMM_COLUMNS } from './constants';
import { InlineEditContext, InlineEditRowItem, InlineEditValue } from './inline-editing/InlineEditContext';
import { SchemaPropertyMap } from './types';
import { generateColumnDefs } from './utils';

type ServerCommProps = FlashContextInterface;

const serverCommService = new ServerCommunicationService();
const schemaService = new SchemaService();
const resourceName = 'server-communications';

const ServerCommunicationPage: FunctionComponent<ServerCommProps> = ({flash}) => {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });

    const portfolioId = match?.params?.portfolioId;
    const isMounted = useIsMounted();
    const [ items, setItems ] = useState<ServerCommunication[]>([]);
    const [ schemas, setSchemas ] = useState<SchemaProperty[]>([]);
    const [ schemaMap, setSchemaMap ] = useState<SchemaPropertyMap>({});
    const [ columnDef, setColumnDef ] = useState<ColumnDefinition<ServerCommunication>[]>([]);
    const [ selectedItems, setSelectedItems ] = useState<ServerCommunication[]>([]);
    const [ filteredItems, setFilteredItems ] = useState<ServerCommunication[] | null>(null);
    const [ loading, setLoading ] = useState(true);
    const { updateInlineEditFn } = useContext(InlineEditContext);

    const loadServerCommData = async () => {
        setLoading(true);
        setSelectedItems([]);
        try {
            if (!portfolioId) {
                flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
                return;
            }
            // Load data
            const serverComms = await serverCommService.list(portfolioId);
            if (!isMounted()) {
                return;
            }
            setItems(serverComms);

            // Load schema
            const schemas = await schemaService.listAttributes(resourceName, portfolioId);
            if (!isMounted()) {
                return;
            }
            setSchemas(schemas);
            const serverCommHeaders: SchemaPropertyMap = {};
            schemas.forEach(schema => {
                serverCommHeaders[schema.value] = schema;
            });
            setSchemaMap(serverCommHeaders);

            // Build column definitions
            const columnDefinitions = generateColumnDefs(serverComms, serverCommHeaders, 
                ColumnOrderType.SERVER_COMMUNICATION, EXCLUDED_SERVER_COMM_COLUMNS, true);
            setColumnDef(columnDefinitions);
        } catch (err) {
            if (!isMounted()) {
                return;
            }
            flash.alert(FlashType.error, (err as Error).message, constants.SERVER_COMM_ERROR_LOADING);
            setItems([]);
            setColumnDef([]);
        } finally {
            if (!isMounted()) {
                return;
            }
            setLoading(false);
        }
    };

    useEffect(() => {
        loadServerCommData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portfolioId]);

    const inlineEditFunction = async (rowItem: InlineEditRowItem, columnId: string, updatedValue: InlineEditValue) => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }

        const conditions: ServerCommBulkUpdateParams = {
            fieldName: columnId,
            fieldValue: updatedValue,
            portfolioId,
            serverCommunicationIds: [(rowItem as ServerCommunication).communicationId],
        };

        try {
            await serverCommService.update(conditions);
            const updateSuccessMsg = generatePluralStr(1, constants.SERVER_COMM_RESOURCE_NAME, '', true)
                + ' ' + constants.BULK_UPDATE_SUCCESS_HEADING;
            flash.alert(FlashType.success, updateSuccessMsg, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.SERVER_COMM_EDITING_ERROR_HEADING);
        } finally {
            await loadServerCommData();
        }
    };

    useEffect(() => {
        updateInlineEditFn(inlineEditFunction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Delete multiple items. If items are selected, they will be deleted. If there are no selected items,
     * then all filtered items will be deleted.
     * @returns void
     */
    const onConfirmBulkDeletion = async () => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        try {
            const selectedServerCommIds: string[] = (selectedItems.length || !filteredItems ? selectedItems : filteredItems)
                .map(selectedItem => selectedItem.communicationId);
            const conditions: ServerCommBulkDeleteParams = {
                serverCommunicationIds: selectedServerCommIds,
                portfolioId,
            };
            await serverCommService.deleteServerComms(conditions); 
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.SERVER_COMM_DELETE_ERROR_HEADING);
        } finally {
            await loadServerCommData();
        }
    };

    const onConfirmDeleteAll = async () => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        try {
            await serverCommService.clear(portfolioId); 
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.SERVER_COMM_DELETE_ERROR_HEADING);
        } finally {
            await loadServerCommData();
        }
    };

    const onConfirmBulkEdit = async (
        selectAll: boolean,
        selectedHeader: string,
        updatedValue: string | number | boolean,
    ) => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        const selectedSchema = schemaMap[selectedHeader];
        if (!selectedHeader || !selectedSchema) {
            flash.alert(FlashType.error, constants.BULK_UPDATE_ERROR_CONTENT, constants.SERVER_COMM_EDITING_ERROR_HEADING);
            return;
        }
        const selectedServerCommIds = (selectAll ? (filteredItems ?? items) : selectedItems).map(selectedItem => selectedItem.communicationId);
        
        const conditions: ServerCommBulkUpdateParams = {
            fieldName: selectedHeader,
            fieldValue: updatedValue,
            portfolioId,
            serverCommunicationIds: selectedServerCommIds,
        };

        try {
            await serverCommService.update(conditions);
            const updateSuccessMsg = generatePluralStr(selectedServerCommIds.length || items.length, constants.SERVER_COMM_RESOURCE_NAME, '', true)
                + ' ' + constants.BULK_UPDATE_SUCCESS_HEADING;
            flash.alert(FlashType.success, updateSuccessMsg, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.SERVER_COMM_EDITING_ERROR_HEADING);
        } finally {
            await loadServerCommData();
        }
    };

    const defaultSortingState: SortingState<ServerCommunication> = {
        isDescending: false,
        sortingColumn: {
            sortingField: 'sourceServerId',
        },
    };

    const filterChanged = (items: readonly ServerCommunication[] | null) => {
        setFilteredItems(deepCopy(items));
    };

    return (
        <SpaceBetween size="m">
            <AWSCATTable
                resourceName={constants.SERVER_COMM_TABLE_HEADER}
                resourceType='serverCommunication'
                items={items}
                importWhenEmpty={true}
                columnDefinitions={columnDef}
                loading={loading}
                resizeable={true}
                autoGenerateVisibleContent={true}
                onSelectionChange={setSelectedItems as () => void}
                onFilterChange={filterChanged}
                headerActions={
                    <PortfolioTableActionGroup
                        resourceType='serverCommunication'
                        resourceName={constants.SERVER_COMM_RESOURCE_NAME}
                        resourceCount={items.length}
                        selectionCount={selectedItems.length}
                        hasFilter={!!filteredItems}
                        filteredCount={filteredItems?.length ?? 0}
                        loading={loading}
                        onConfirmDeletion={onConfirmBulkDeletion}
                        onConfirmDeleteAll={onConfirmDeleteAll}
                        onConfirmBulkEdit={onConfirmBulkEdit}
                        schemaProperties={schemas}
                        schemaPropertyMap={schemaMap}
                    />
                }
                selectionType='multi'
                defaultSortBy={defaultSortingState}
                useDefaultDownload
            />
        </SpaceBetween>
    );
};

export default withFlashContext(ServerCommunicationPage);
